import { isNil } from 'lodash';

export type RateClassificationT = 'ten99' | 'w2.local' | 'w2.travel';

interface BlendedOrNonBlendedRateSettings {
  blended_rate_markup: number;
  charge_overtime_multiplier: number;
  payout_overtime_multiplier: number;
  charge_max_daily_regular_hours: number;
  payout_max_daily_regular_hours: number;
  payout_max_daily_overtime_hours: number;
  charge_max_daily_overtime_hours: number;
  charge_double_overtime_multiplier: number;
  payout_double_overtime_multiplier: number;
}

type RateSpecificConfigurations = {
  charge_rate_cents?: number;
  blended_rates: BlendedOrNonBlendedRateSettings;
  non_blended_rates: BlendedOrNonBlendedRateSettings;
  use_blended_rate: boolean;
  total_margin_percent_w2?: number;
  base_margin_percent_w2?: number;
  margin_percent_1099?: number;
  margin_percent_w2_travel?: number;
  pro_rate_cents?: number;
  invoicing_markup?: number;
  operational_markup?: number;
  misc_markup?: number;
};

interface RateSettings {
  charge_max_weekly_regular_hours?: number;
  charge_seventh_day_rule_enabled?: boolean;
  payout_seventh_day_rule_enabled?: boolean;
  payout_max_weekly_regular_hours?: number;
}

export interface W2Burdens {
  federal_unemployment_percent_burden_multiplier?: number;
  medicare_burden_multiplier?: number;
  social_security_burden_multiplier?: number;
  state_unemployment_percent_burden_multiplier?: number;
}
interface W2Settings extends RateSettings, W2Burdens {
  local: RateSpecificConfigurations;
  travel: RateSpecificConfigurations;
}

type Ten99Settings = RateSettings & RateSpecificConfigurations;

interface TaxClassificationSettings {
  ten99: Ten99Settings;
  w2: W2Settings;
}

interface RateSettingTaxes {
  with_markup_settings?: TaxClassificationSettings;
  settings: TaxClassificationSettings;
  location?: {
    primary_billing_group: {
      plan: string;
    };
  };
}

export class RateSettingsDisplayHelper {
  constructor(private rateSetting: RateSettingTaxes) {}

  getPlan() {
    return this.rateSetting.location?.primary_billing_group?.plan || 'standard';
  }

  getNestedObject() {
    if (this.getPlan() === 'enterprise_w2') {
      return 'w2.local';
    }

    return 'ten99';
  }

  getBlendedRateMarkup(forceNestedObject?: string): number | null {
    const nestedObject = forceNestedObject || this.getNestedObject();
    const settings = this.getSettingsForClassification(nestedObject);

    return !isNil(settings?.blended_rates?.blended_rate_markup)
      ? settings?.blended_rates?.blended_rate_markup
      : null;
  }

  getMultiplier(classification?: RateClassificationT) {
    return 1 + this.getBlendedRateMarkup(classification);
  }

  getSettingsForClassification(
    nestedObject: string,
    settingsSource?: TaxClassificationSettings,
  ): RateSettings & RateSpecificConfigurations {
    const [taxClassification, subHash] = nestedObject?.split('.');
    const settings = settingsSource || this.rateSetting.settings;
    const ratesObj = subHash
      ? settings?.[taxClassification]?.[subHash]
      : settings?.[taxClassification];

    return ratesObj;
  }

  getActiveRate(forceNestedObject?: string): '8-hour-rate' | 'blended-rate' | '' {
    const nestedObject = forceNestedObject || this.getNestedObject();
    const taxClassificationNestedObject = this.getSettingsForClassification(nestedObject);

    const useBlendedRate = taxClassificationNestedObject?.use_blended_rate;
    if (isNil(useBlendedRate)) {
      return null;
    }
    return useBlendedRate ? 'blended-rate' : '8-hour-rate';
  }

  getActiveRateObjKey(forceNestedObject?: RateClassificationT) {
    return this.getActiveRate(forceNestedObject) === '8-hour-rate'
      ? 'non_blended_rates'
      : 'blended_rates';
  }

  getActiveRateStructureLabel(forceNestedObject?: RateClassificationT): '8hr' | '12hr' | '' {
    const nestedObject = forceNestedObject || this.getNestedObject();
    const activeRateStructureLabel = this.getActiveRate(nestedObject);
    if (isNil(activeRateStructureLabel)) {
      return '';
    }
    return activeRateStructureLabel === '8-hour-rate' ? '8hr' : '12hr';
  }

  getActiveChargeRateCents(classification?: RateClassificationT, withMarkup?: boolean): number {
    const applicableClassification = classification || this.getNestedObject();
    const applicableSettings = withMarkup
      ? this.rateSetting.with_markup_settings || this.rateSetting.settings
      : this.rateSetting.settings;

    const settingsForClassification = this.getSettingsForClassification(
      applicableClassification,
      applicableSettings,
    );

    return settingsForClassification?.charge_rate_cents;
  }

  getChargeRateCents(classification?: RateClassificationT, forceNestedObject?: string): number {
    const activeRate = this.getActiveRate(forceNestedObject);
    const chargeRateCents = this.getActiveChargeRateCents(classification);

    if (activeRate === '8-hour-rate') {
      return chargeRateCents;
    }

    const multiplier = this.getMultiplier(classification);

    return chargeRateCents * multiplier;
  }

  get12hRate(classification: RateClassificationT) {
    const multiplier = this.getMultiplier(classification);
    const chargeRateCents = this.getActiveChargeRateCents(classification);

    return chargeRateCents * multiplier;
  }

  format12hMarkupDisplay(classification?: RateClassificationT) {
    const blendedRateMarkup = this.getBlendedRateMarkup(classification);
    return !isNil(blendedRateMarkup) ? `${(blendedRateMarkup * 100).toFixed(2)}%` : '';
  }

  getW2Burdens(): W2Burdens {
    const w2Settings = this.rateSetting.settings?.w2;

    return {
      federal_unemployment_percent_burden_multiplier:
        w2Settings?.federal_unemployment_percent_burden_multiplier ?? null,
      medicare_burden_multiplier: w2Settings?.medicare_burden_multiplier ?? null,
      social_security_burden_multiplier: w2Settings?.social_security_burden_multiplier ?? null,
      state_unemployment_percent_burden_multiplier:
        w2Settings?.state_unemployment_percent_burden_multiplier ?? null,
    };
  }
}
